<template>
  <InfuseTable
    :click-permission-id="networkProfilePermissions.GET"
    :columns="columns"
    :rows="rows"
    :loading="loading"
    :editable="editable"
    :removable="removable"
    :search="search"
    :has-more-items="hasMoreItems"
    :edit-permission-id="networkProfilePermissions.UPDATE"
    :remove-permission-id="networkProfilePermissions.DELETE"
    @click-row="(row: NetworkProfile) => emit('click-row', row)"
    @load="emit('load')"
    @remove="(row: NetworkProfile) => emit('remove', row)"
    @edit="(row: NetworkProfile) => emit('edit', row)"
    @search="(query: string) => emit('search', query)"
  >
    <template #actions>
      <InfuseButton
        data-testid="page-create-button"
        v-permission="{ id: networkProfilePermissions.CREATE, hide: false }"
        v-if="createEnabled"
        class="w-64"
        size="medium"
        @click="emit('create')"
      >
        {{ t('e_connector.networkProfiles.table.create_button') }}
      </InfuseButton>
    </template>

    <template #ipRanges="{ data }">
      <div class="max-w-[300px] truncate">{{ data || '-' }}</div>
    </template>
  </InfuseTable>
</template>

<script lang="ts" setup>
import { NetworkProfile, networkProfilePermissions } from '@infuse/api/e-connector/network-profile';
import { InfuseButton, InfuseTable } from '@infuse/ui/components';
import { vPermission } from '@infuse/iam/permission/directives';
import type { UiTableColumn } from '@infuse/ui/domain';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { mapIpRangeToString } from '@infuse/e-connector/network-profile/utils';

interface Emits {
  (e: 'click-row', row: NetworkProfile): void;
  (e: 'create'): void;
  (e: 'load'): void;
  (e: 'remove', row: NetworkProfile): void;
  (e: 'edit', row: NetworkProfile): void;
  (e: 'search', query: string): void;
}

const emit = defineEmits<Emits>();

interface Props {
  createEnabled: boolean;
  editable: boolean;
  loading?: boolean;
  removable?: boolean;
  rows: NetworkProfile[];
  searchEnabled: boolean;
  searchQuery?: string;
  hasMoreItems: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  createEnabled: false,
  editable: false,
  loading: false,
  removable: false,
  searchEnabled: true,
  searchQuery: ''
});

const { t } = useI18n();

const search = computed(() =>
  !props.searchEnabled
    ? undefined
    : {
        placeholder: t('e_connector.networkProfiles.table.search'),
        query: props.searchQuery
      }
);

const rows = computed(() => props.rows.map((row) => ({ ...row, ipRangesString: mapIpRangeToString(row.ipRanges) })));

const columns: UiTableColumn[] = [
  {
    name: 'name',
    label: () => t('e_connector.common.networkProfile.name'),
    field: 'name',
    bold: true
  },
  {
    name: 'ipRangesString',
    label: () => t('e_connector.common.networkProfile.ipRanges'),
    field: 'ipRangesString'
  }
];
</script>
